import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBoxOpen,
  faChevronDown,
  faDroplet,
  faHandHoldingMedical,
  faOilCan,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { COMET_GLOVE_DEFINITIONS } from '../data/cometGloves'
import { MEDICAL_SUPPLY_DEFINITIONS } from '../data/medicalSupplies'
import { GEKKO_PRODUCTS } from './sarung-tangan-gekko'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import { getStorefrontConfig, listStorefrontCategories, toExternalImage } from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig } from '../lib/dropify'
import {
  INDEXABLE_ROBOTS,
  absoluteUrl,
  brandTitle,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-nitrile'
const PAGE_CATEGORY = 'Sarung Tangan Nitrile'
const CARD_IMAGE_TRANSFORM = { width: 560, height: 560, fitIn: true, format: 'webp', quality: 74 } as const

// Two unrelated product families both get called "sarung tangan nitrile" in
// search: disposable exam gloves sold per box, and reusable knit work gloves
// with a nitrile palm coating sold per lusin. This page exists to catch that
// query and route buyers to the right one before they land on a mismatched LP.
const DISPOSABLE_NITRILE = MEDICAL_SUPPLY_DEFINITIONS.filter((item) => item.material === 'Nitrile')

type WorkGlove = {
  brand: 'Gekko Grip' | 'COMET'
  name: string
  slug: string
  image: string
  coating: string
  standard?: string
  useCase: string
  href: string
}

const GEKKO_NITRILE: WorkGlove[] = GEKKO_PRODUCTS.filter((item) => item.coating.startsWith('Nitrile')).map((item) => ({
  brand: 'Gekko Grip',
  name: item.name,
  slug: item.slug,
  image: item.image,
  coating: item.coating,
  useCase: item.fit,
  href: '/sarung-tangan-gekko',
}))

const COMET_NITRILE: WorkGlove[] = COMET_GLOVE_DEFINITIONS.filter((item) => item.slug.includes('nitrile')).map((item) => ({
  brand: 'COMET',
  name: item.name,
  slug: item.slug,
  image: item.image,
  coating: item.construction,
  standard: item.standard,
  useCase: item.useCase,
  href: '/sarung-tangan-comet',
}))

const WORK_NITRILE: WorkGlove[] = [...GEKKO_NITRILE, ...COMET_NITRILE]

const DECISION_GUIDE = [
  {
    icon: faHandHoldingMedical,
    title: 'Nitrile sekali pakai',
    points: [
      'Tipis, dijual per box isi 100 pcs, dipakai lalu dibuang.',
      'Untuk pemeriksaan medis, laboratorium, dan penanganan barang di toko alkes.',
      `Merek di Dropify: ${Array.from(new Set(DISPOSABLE_NITRILE.map((item) => item.name.split(' ')[0]))).join(', ')}.`,
    ],
  },
  {
    icon: faOilCan,
    title: 'Nitrile kerja (rajut)',
    points: [
      'Rajutan dengan telapak dan jari dilapisi nitrile, dijual per lusin, dipakai berulang.',
      'Untuk bengkel, pabrik, perawatan mesin, dan pekerjaan di permukaan berminyak.',
      'Merek di Dropify: Gekko Grip dan COMET.',
    ],
  },
]

const TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Saya cari nitrile tapi belum tahu yang sekali pakai atau yang kerja. Bagaimana memastikannya?',
    a: 'Ceritakan pemakaiannya ke admin: untuk pemeriksaan atau kontak singkat berarti sekali pakai per box, untuk bengkel atau pabrik dengan pemakaian berulang berarti nitrile kerja per lusin. Admin membantu mengarahkan ke produk yang tepat.',
  },
  {
    q: 'Bisakah pesan nitrile sekali pakai dan nitrile kerja dalam satu chat?',
    a: 'Bisa dalam satu chat, tetapi jumlahnya dihitung terpisah karena satuannya beda. Nitrile sekali pakai dihitung per box, nitrile kerja dihitung per lusin. Sebutkan kebutuhan masing-masing agar admin bisa menyusun dua quotation.',
  },
  {
    q: 'Klinik atau toko alkes saya perlu stok rutin nitrile sekali pakai. Bisa restock berkala?',
    a: 'Bisa. Simpan nama produk dari pesanan pertama, lalu kirim ulang daftar yang sama saat butuh restock. Admin mengecek stok dan mengirim quotation baru setiap kali.',
  },
  {
    q: 'Tim bengkel saya belum pernah pakai nitrile kerja Gekko atau COMET. Bagaimana menilai kecocokannya?',
    a: 'Sampaikan jenis pekerjaan dan risiko utamanya, misalnya permukaan berminyak, bahan kimia, atau benturan. Admin membantu memilih seri, lalu Anda bisa minta foto barang aktual sebelum order dalam jumlah besar.',
  },
  {
    q: 'Apakah nitrile kerja COMET punya dokumen standar untuk pengadaan pabrik?',
    a: 'CG 905 GY mencantumkan EN 388:2016 3141X. NF 1513 mencantumkan EN 388:2016 4001X, EN 374-1:2016 Type A, dan EN 374-5:2016. Sebutkan standar yang diwajibkan tim K3 agar admin dapat memeriksa dokumen untuk seri yang dipilih.',
  },
]

export const NITRILE_FAQ: FaqItem[] = [
  {
    q: 'Apa beda nitrile sekali pakai dan nitrile kerja?',
    a: 'Nitrile sekali pakai adalah sarung tangan tipis yang dijual per box dan dibuang setelah dipakai, umum untuk kebutuhan medis. Nitrile kerja adalah sarung tangan rajut dengan telapak berlapis nitrile, dipakai berulang, dan dijual per lusin untuk kerja bengkel atau pabrik.',
  },
  {
    q: 'Berapa MOQ masing-masing jenis?',
    a: 'Nitrile sekali pakai minimum 10 box, isi 100 pcs per box. Nitrile kerja Gekko Grip minimum 50 lusin, 12 pasang per lusin. Nitrile kerja COMET mengikuti MOQ per seri, konfirmasi ke admin.',
  },
  {
    q: 'Merek apa saja yang tersedia?',
    a: `Nitrile sekali pakai: ${DISPOSABLE_NITRILE.map((item) => item.name).join(', ')}. Nitrile kerja: 3 varian Gekko Grip dan 2 seri COMET (CG 905 GY, NF 1513 Chemical Nitrile).`,
  },
  {
    q: 'Ukuran apa saja yang tersedia untuk nitrile kerja?',
    a: 'Gekko Grip mengikuti ukuran standar sarung tangan kerja, sebutkan kebutuhan Anda ke admin. COMET CG 905 GY tersedia M sampai XL, NF 1513 tersedia S sampai L.',
  },
  {
    q: 'Bagaimana cara order?',
    a: 'Kirim jenis nitrile yang dicari (sekali pakai atau kerja), nama produk atau seri, ukuran jika relevan, jumlah, dan kota tujuan melalui WhatsApp +62 857-0401-1028. Admin mengecek stok, mengirim foto barang aktual, lalu memberi harga dan ongkir.',
  },
]

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  className,
}: {
  message: string
  label: string
  placement: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function DisposableCard({ glove }: { glove: (typeof DISPOSABLE_NITRILE)[number] }) {
  const message = `Halo Dropify, saya mau cek ${glove.name}. Ukuran: ___. Kebutuhan: ___ box. Kota tujuan: ___. Bisa kirim harga per box dan stoknya?`
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-sky-100 bg-white">
      <a href={`/produk/${glove.slug}`} className="block bg-sky-50/60">
        <img
          src={toExternalImage(glove.image, CARD_IMAGE_TRANSFORM)}
          alt={glove.name}
          width="560"
          height="560"
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-contain"
        />
      </a>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-sky-900">Nitrile</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.powder}</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.packSize} pcs/box</span>
          {glove.color && (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.color}</span>
          )}
        </div>
        <h3 className="mt-3 flex-1 text-base font-black tracking-tight text-slate-900">
          <a href={`/produk/${glove.slug}`} className="hover:text-sky-700">{glove.name}</a>
        </h3>
        <a
          href={waLink(message)}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('landing_page', { cta_placement: 'product_card', lp_category: PAGE_CATEGORY, lead_item_name: glove.name })}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-2.5 text-sm font-black text-white transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
          Cek harga per box
        </a>
      </div>
    </article>
  )
}

function WorkGloveCard({ glove }: { glove: WorkGlove }) {
  const message = `Halo Dropify, saya mau cek ${glove.name}. Kebutuhan: ___ lusin. Kota tujuan: ___. Bisa kirim harga dan stoknya?`
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-sky-100 bg-white">
      <a
        href={glove.href}
        className="block bg-sky-50/60"
        onClick={() => trackSelectItem({ item_id: glove.slug, item_name: glove.name, item_category: PAGE_CATEGORY }, 'Sarung tangan nitrile')}
      >
        <img
          src={glove.image.startsWith('http') ? toExternalImage(glove.image, CARD_IMAGE_TRANSFORM) : glove.image}
          alt={glove.name}
          width="560"
          height="560"
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-contain"
        />
      </a>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-[#0c2f45] px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-sky-100">{glove.brand}</span>
          {glove.standard && (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.standard}</span>
          )}
        </div>
        <h3 className="mt-3 flex-1 text-base font-black tracking-tight text-slate-900">
          <a href={glove.href} className="hover:text-sky-700">{glove.name}</a>
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{glove.useCase}</p>
        <a
          href={waLink(message)}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('landing_page', { cta_placement: 'product_card', lp_category: PAGE_CATEGORY, lead_item_name: glove.name })}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-2.5 text-sm font-black text-white transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
          Cek harga per lusin
        </a>
      </div>
    </article>
  )
}

export const Route = createFileRoute('/sarung-tangan-nitrile')({
  loader: async () => {
    const [config, categories, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])
    return { config, categories, proofImages, newsPosts }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Nitrile, Sekali Pakai dan Kerja')
    const description =
      'Sarung tangan nitrile di Dropify ada dua jenis: sekali pakai per box (Shamrock, Disposable) dan kerja rajut per lusin (Gekko Grip, COMET). Cek jenis yang sesuai lalu tanya stok via WhatsApp.'
    const heroImage = toExternalImage(DISPOSABLE_NITRILE[0].image, CARD_IMAGE_TRANSFORM)

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: heroImage },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: heroImage },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Sarung Tangan Nitrile', path: PAGE_PATH },
            ]),
          ),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...NITRILE_FAQ, ...TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Sarung tangan nitrile di Dropify',
            itemListElement: [
              ...DISPOSABLE_NITRILE.map((glove, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@type': 'Thing',
                  name: glove.name,
                  sku: glove.sku,
                  image: toExternalImage(glove.image, CARD_IMAGE_TRANSFORM),
                  url: absoluteUrl(`/produk/${glove.slug}`),
                },
              })),
              ...WORK_NITRILE.map((glove, index) => ({
                '@type': 'ListItem',
                position: DISPOSABLE_NITRILE.length + index + 1,
                item: {
                  '@type': 'Thing',
                  name: glove.name,
                  image: glove.image.startsWith('http') ? toExternalImage(glove.image, CARD_IMAGE_TRANSFORM) : absoluteUrl(glove.image),
                  url: absoluteUrl(glove.href),
                  brand: { '@type': 'Brand', name: glove.brand },
                },
              })),
            ],
          }),
        },
      ],
    }
  },
  component: NitrileRoute,
})

function NitrileRoute() {
  const { config, categories, proofImages, newsPosts } = Route.useLoaderData()
  return <NitrilePage config={config} categories={categories} proofImages={proofImages} newsPosts={newsPosts} />
}

export function NitrilePage({
  config = null,
  categories = [],
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mau cek sarung tangan nitrile. Jenis: ___ (sekali pakai/kerja). Kebutuhan: ___. Kota tujuan: ___. Bisa bantu arahkan dan cek stoknya?'

  return (
    <div className="min-h-screen bg-[#f7fbfe] text-[#0c2436]">
      <div className="bg-sky-200 px-4 py-2 text-center text-xs font-black text-[#0c2f45] sm:text-sm">
        {DISPOSABLE_NITRILE.length + WORK_NITRILE.length} pilihan nitrile · 2 jenis, satuan beda · Kirim dari Surabaya
      </div>

      <header className="border-b border-white/10 bg-[#0c2f45] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Tanyakan kepada admin"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-sky-200/60 px-4 py-2.5 text-sm font-bold text-sky-50 transition-colors hover:bg-sky-200 hover:text-[#0c2f45]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#0c2f45] text-white">
          <div aria-hidden className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-sky-400/10 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:py-20">
            <p className="inline-flex rounded-full bg-sky-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#0c2f45]">
              Dua jenis nitrile, satuan jual berbeda
            </p>
            <h1 className="mt-6 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Sarung tangan nitrile: pilih sekali pakai atau kerja dulu
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-sky-50/80 md:text-lg">
              Nitrile sekali pakai tipis, dijual per box, untuk kebutuhan medis. Nitrile kerja berupa rajutan berlapis nitrile, dijual per lusin, untuk bengkel dan pabrik. Pilih dulu sebelum menghubungi admin agar quotation-nya tepat.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#sekali-pakai" className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800">
                Nitrile sekali pakai ({DISPOSABLE_NITRILE.length})
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
              </a>
              <a href="#kerja" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                Nitrile kerja ({WORK_NITRILE.length})
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-4 text-sm text-sky-100/70">Belum yakin yang mana? Kirim cerita pemakaiannya, admin bantu arahkan.</p>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section className="bg-[#f7fbfe] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Keputusan pertama</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Dua produk, dua cara pakai</h2>
            </div>
            <div className="mt-9 grid gap-4 md:grid-cols-2">
              {DECISION_GUIDE.map((item) => (
                <article key={item.title} className="rounded-2xl border border-sky-100 bg-white p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0c2f45] text-sky-200">
                      <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-black tracking-tight">{item.title}</h3>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {item.points.map((point) => (
                      <li key={point} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="sekali-pakai" className="scroll-mt-6 border-y border-sky-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">{`${DISPOSABLE_NITRILE.length} pilihan tersedia`}</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Nitrile sekali pakai, per box</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Untuk pemeriksaan medis, laboratorium, dan toko alkes. Kalau perlu bandingkan dengan latex sekaligus, lihat{' '}
                <a href="/sarung-tangan-latex-nitrile" className="font-bold text-sky-700 hover:underline">halaman latex &amp; nitrile</a>.
              </p>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {DISPOSABLE_NITRILE.map((glove) => (
                <DisposableCard key={glove.sku} glove={glove} />
              ))}
            </div>
          </div>
        </section>

        <section id="kerja" className="scroll-mt-6 bg-[#f7fbfe] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">{`${WORK_NITRILE.length} pilihan tersedia`}</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Nitrile kerja, per lusin</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Rajutan dengan telapak berlapis nitrile, dipakai berulang untuk bengkel, pabrik, dan perawatan mesin.
              </p>
            </div>

            <h3 className="mt-10 text-lg font-black uppercase tracking-wide text-slate-500">Gekko Grip ({GEKKO_NITRILE.length})</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {GEKKO_NITRILE.map((glove) => (
                <WorkGloveCard key={glove.slug} glove={glove} />
              ))}
            </div>

            <h3 className="mt-12 text-lg font-black uppercase tracking-wide text-slate-500">COMET ({COMET_NITRILE.length})</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {COMET_NITRILE.map((glove) => (
                <WorkGloveCard key={glove.slug} glove={glove} />
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-sky-100 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Cara belinya</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">MOQ beda per jenis, satu chat tetap cukup</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Sebutkan jenis nitrile yang dicari, nama produk atau seri, jumlah, dan kota tujuan. Admin menyusun quotation sesuai satuan masing-masing jenis.
              </p>
              <WhatsAppButton
                message={heroMessage}
                label="Kirim kebutuhan Anda"
                placement="moq_section"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <dl className="divide-y divide-sky-100 rounded-2xl border border-sky-100 bg-white px-6">
              <div className="grid gap-1 py-5 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Nitrile sekali pakai</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">MOQ 10 box, isi 100 pcs per box.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Nitrile kerja Gekko Grip</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">MOQ 50 lusin, isi 12 pasang per lusin.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Nitrile kerja COMET</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">MOQ mengikuti seri, konfirmasi ke admin.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Pembayaran</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Transfer bank BCA atau Mandiri. Dropify belum melayani COD.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Proses &amp; kirim</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Admin mengonfirmasi waktu penyiapan pesanan. Estimasi perjalanan dihitung dari kota tujuan dan layanan ekspedisi.</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="bg-[#f7fbfe] py-14 md:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Halaman terkait</p>
              <h2 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">Lihat pilihan lain untuk kebutuhan yang berbeda</h2>
            </div>
            <div className="mt-7 grid gap-4 md:grid-cols-3">
              <a href="/sarung-tangan-latex-nitrile" className="group rounded-2xl border border-sky-100 bg-white p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faDroplet} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Sarung tangan latex & nitrile</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">Bandingkan latex dan nitrile sekali pakai sekaligus, termasuk pilihan powdered dan warna.</p>
              </a>
              <a href="/perlengkapan-medis" className="group rounded-2xl border border-sky-100 bg-white p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faBoxOpen} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Perlengkapan medis grosir</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">Paket pengadaan untuk rumah sakit, klinik, dan toko alkes.</p>
              </a>
              <a href="/supplier-apd-pabrik" className="group rounded-2xl border border-sky-100 bg-white p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faOilCan} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Supplier APD pabrik</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">APD tangan dan pernapasan untuk kebutuhan K3 pabrik, termasuk seri COMET berstandar EN.</p>
              </a>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">FAQ nitrile</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum menghubungi admin</h2>
            </div>
            <div className="mt-9 divide-y divide-sky-100 border-y border-sky-100">
              {NITRILE_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-[#0c2436]">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-sky-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-200 py-14 text-[#0c2f45]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Mulai dari jenisnya</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebutkan jenis, produk, jumlah, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Admin membalas dengan harga, foto stok, dan perkiraan ongkir dari Surabaya.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation nitrile"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#0c2f45] px-7 py-4 text-base font-black text-white transition-colors hover:bg-[#0c2436]"
            />
          </div>
        </section>

        <BrandTrustSections
          brandName="nitrile"
          itemName="sarung tangan nitrile"
          images={proofImages}
          questions={TRUST_QUESTIONS}
        />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
